/**
 * Leitura do espelho da qualidade do ar publicado pela máquina do agendador
 * (`AmaView-dados/agendador/ar`).
 *
 * O navegador não fala com nenhuma das fontes: as redes de sensores só dão a
 * leitura do momento (o espelho monta a série a cada 5 min) e a do Acre não
 * manda CORS utilizável; a PurpleAir e a OpenAQ pedem chave; o Open-Meteo tem
 * limite de uso por IP (o espelho baixa a grade do modelo poucas vezes por
 * dia, para todo mundo) e o MonitorAr vai junto por robustez. As funções
 * `ler*` são puras (testadas em `ar.test.ts`).
 */
import { fetchWithRetry } from '../lib/fetchRetry';
import { HOUR_MS } from '../lib/time';
import { lonLatToMap } from '../map/projection';
import { faixaDaEstacao, faixaDoMonitorAr, faixaIqar } from './faixas';
import { TOLERANCIA_SENSOR_MS } from './tempo';
import type { Cams, CamsSeries, Estacao, Fonte, FonteSensor, IqarHora, Poluente, Pontos, Sensor, VariavelCams } from './types';

/** Onde a máquina publica. `VITE_AR_URL` aponta para outro lugar (vazio desliga a camada). */
export const BASE_AR = (import.meta.env.VITE_AR_URL ?? 'https://147.15.84.134/ar').replace(/\/$/, '');

export const UFAC_URL = 'https://acrequalidadedoar.ufac.br/';
export const REDEAR_URL = 'https://redear.org.br/';
export const PURPLEAIR_URL = 'https://www2.purpleair.com/';
export const AIRGRADIENT_URL = 'https://www.airgradient.com/map/';
export const OPENAQ_URL = 'https://explore.openaq.org/';
export const MONITORAR_URL = 'https://monitorar.mma.gov.br/';
export const OPEN_METEO_URL = 'https://open-meteo.com/en/docs/air-quality-api';
export const CAMS_URL = 'https://atmosphere.copernicus.eu/';

/** Atribuição exigida pela licença do CAMS (Copernicus). */
export const CAMS_ATRIBUICAO = 'Contains modified Copernicus Atmosphere Monitoring Service information';
/** Atribuição exigida pelos termos da PurpleAir sempre que há dado de um sensor dela. */
export const PURPLEAIR_ATRIBUICAO = 'Data from PurpleAir';

export const CAMS_ATRIBUICAO_MAPA = `Modelo: CAMS/Copernicus via Open-Meteo — ${CAMS_ATRIBUICAO}`;

/** As redes na ordem em que aparecem (painel, créditos): da mais antiga na camada às opcionais. */
export const FONTES_SENSOR: readonly FonteSensor[] = ['ufac', 'redear', 'purpleair', 'airgradient', 'openaq'];

/** Nome curto da rede (contagem, créditos). */
export const NOME_REDE: Readonly<Record<FonteSensor | 'monitorar', string>> = {
  ufac: 'UFAC/MPAC',
  redear: 'RedeAr (IPAM/UFPA)',
  purpleair: 'PurpleAir',
  airgradient: 'AirGradient',
  openaq: 'OpenAQ',
  monitorar: 'MonitorAr/MMA',
};

/** Nome longo, para a ficha. */
export const NOME_REDE_LONGO: Readonly<Record<FonteSensor, string>> = {
  ufac: 'Rede de Qualidade do Ar do Acre (UFAC/MPAC)',
  redear: 'RedeAr (IPAM/UFPA)',
  purpleair: 'PurpleAir (API, consulta de 2 em 2 h)',
  airgradient: 'AirGradient (mapa público)',
  openaq: 'OpenAQ',
};

export const URL_REDE: Readonly<Record<FonteSensor, string>> = {
  ufac: UFAC_URL,
  redear: REDEAR_URL,
  purpleair: PURPLEAIR_URL,
  airgradient: AIRGRADIENT_URL,
  openaq: OPENAQ_URL,
};

/**
 * O link da fonte do ponto — só dela, e não das outras redes que repassam o
 * mesmo aparelho: o sensor no mapa da PurpleAir, o local na OpenAQ, o site
 * da rede nas demais.
 */
export function linkDoSensor(s: Pick<Sensor, 'fonte' | 'ref'>): string {
  if (s.fonte === 'purpleair') return `https://map.purpleair.com/?select=${s.ref}`;
  if (s.fonte === 'openaq') return `https://explore.openaq.org/locations/${s.ref}`;
  return URL_REDE[s.fonte];
}

/** Rótulo do link da fonte: "PurpleAir 25531", "OpenAQ 5009964", ou o nome da rede. */
export function rotuloRef(s: Pick<Sensor, 'fonte' | 'ref'>): string {
  if (s.fonte === 'purpleair') return `PurpleAir ${s.ref}`;
  if (s.fonte === 'openaq') return `OpenAQ ${s.ref}`;
  return NOME_REDE_LONGO[s.fonte];
}

/**
 * As redes com algum ponto vivo (dado nas últimas 48 h), na ordem fixa, e se
 * há aparelho PurpleAir entre eles — para os créditos da exportação e a
 * atribuição do mapa. Sem nada vivo, as redes que existem no arquivo.
 */
export function redesPresentes(
  pontos: Pick<Pontos, 'sensores' | 'estacoes'>,
  agora: number = Date.now(),
): { redes: string[]; purpleAir: boolean } {
  const vivo = (t: number | null) => t !== null && Number.isFinite(t) && agora - t <= 48 * HOUR_MS;
  let sensores = pontos.sensores.filter((s) => vivo(s.ultima.t));
  if (sensores.length === 0) sensores = [...pontos.sensores];
  let estacoes = pontos.estacoes.filter((e) => vivo(e.ultima));
  if (estacoes.length === 0) estacoes = [...pontos.estacoes];
  const tem = new Set<FonteSensor>();
  for (const s of sensores) {
    tem.add(s.fonte);
    for (const t of s.tambem) tem.add(t);
  }
  const redes = FONTES_SENSOR.filter((f) => tem.has(f)).map((f) => NOME_REDE[f]);
  if (estacoes.length) redes.push(NOME_REDE.monitorar);
  return { redes, purpleAir: sensores.some((s) => s.pa) };
}

/** Atribuição da camada no mapa (e na exportação nativa). */
export function atribuicaoAr(pontos: Pick<Pontos, 'sensores' | 'estacoes'>, agora?: number): string {
  const { redes, purpleAir } = redesPresentes(pontos, agora);
  if (!redes.length) return 'Qualidade do ar: UFAC/MPAC · MonitorAr/MMA';
  return `Qualidade do ar: ${redes.join(' · ')}${purpleAir ? ` · ${PURPLEAIR_ATRIBUICAO}` : ''}`;
}

const FONTES_VALIDAS = new Set<string>(FONTES_SENSOR);

type Cru = Record<string, unknown>;

function num(x: unknown): number | null {
  return typeof x === 'number' && Number.isFinite(x) ? x : null;
}

function texto(x: unknown): string | null {
  return typeof x === 'string' && x.trim() ? x.trim() : null;
}

function serieNum(x: unknown, n: number): (number | null)[] {
  // Série com tamanho errado é dado quebrado: melhor sem série que na hora errada.
  if (!Array.isArray(x) || x.length !== n) return new Array(n).fill(null);
  return x.map(num);
}

function projetar(lon: number, lat: number): [number, number] | null {
  const [mx, my] = lonLatToMap(lon, lat);
  return Number.isFinite(mx) && Number.isFinite(my) ? [mx, my] : null;
}

/** IQAr de cada hora: o pior poluente naquela hora, com a classificação oficial dele. */
export function iqarPorHora(poluentes: readonly Poluente[], horas: number): (IqarHora | null)[] {
  const out: (IqarHora | null)[] = new Array(horas).fill(null);
  for (let h = 0; h < horas; h++) {
    let melhor: IqarHora | null = null;
    for (const p of poluentes) {
      const v = p.iqar[h];
      if (typeof v !== 'number' || !Number.isFinite(v)) continue;
      if (melhor && v <= melhor.iqar) continue;
      const faixa = faixaDaEstacao(v, p.classe[h] ?? null);
      if (faixa) melhor = { iqar: v, faixa, poluente: p.nome };
    }
    out[h] = melhor;
  }
  return out;
}

export function lerPontos(cru: unknown): Pontos {
  const r = cru as Cru;
  if (!r || typeof r !== 'object') throw new Error('pontos.json vazio');
  const s = r.sensores as Cru | undefined;
  const e = r.estacoes as Cru | undefined;
  const m = r.municipios as Cru | undefined;
  if (!s || !Array.isArray(s.lista) || !e || !Array.isArray(e.lista)) throw new Error('pontos.json sem `sensores` ou `estacoes`');

  const slots = Number(s.slots) || 0;
  const sensores: Sensor[] = [];
  for (const x of s.lista as Cru[]) {
    const lat = num(x.lat);
    const lon = num(x.lon);
    const id = num(x.id);
    const ult = (x.ult ?? {}) as Cru;
    const t = num(ult.t);
    if (lat === null || lon === null || id === null || t === null) continue;
    const p = projetar(lon, lat);
    if (!p) continue;
    // Arquivo de antes da RedeAr (sem `fonte`): todo sensor era da rede do Acre, PurpleAir.
    const fonte = (typeof x.fonte === 'string' && FONTES_VALIDAS.has(x.fonte) ? x.fonte : 'ufac') as FonteSensor;
    const ab = x.ab && typeof x.ab === 'object' ? (x.ab as Cru) : null;
    const met = x.met && typeof x.met === 'object' ? (x.met as Cru) : null;
    const tol = num(x.tol);
    sensores.push({
      tipo: 'sensor',
      id,
      cod: texto(x.cod),
      nome: texto(x.nome),
      municipio: texto(x.mun),
      uf: texto(x.uf) ?? (fonte === 'ufac' ? 'AC' : null),
      dono: texto(x.dono),
      fonte,
      ref: num(x.ref) ?? id,
      pa: typeof x.pa === 'boolean' ? x.pa : fonte === 'ufac',
      tambem: Array.isArray(x.tambem)
        ? (x.tambem as unknown[]).filter((f): f is FonteSensor => typeof f === 'string' && FONTES_VALIDAS.has(f))
        : [],
      lat,
      lon,
      mx: p[0],
      my: p[1],
      ultima: { t, pm: num(ult.pm), flags: num(ult.fl) },
      bruto: ab && (num(ab.a) !== null || num(ab.b) !== null) ? { a: num(ab.a), b: num(ab.b), t: num(ab.t), flags: num(ab.fl) } : null,
      met: met && (num(met.temp) !== null || num(met.ur) !== null) ? { temp: num(met.temp), ur: num(met.ur) } : null,
      outras: Array.isArray(x.outras)
        ? (x.outras as Cru[]).flatMap((o) => {
            const t = num(o?.t);
            const pm = num(o?.pm);
            return typeof o?.fonte === 'string' && FONTES_VALIDAS.has(o.fonte) && t !== null && pm !== null
              ? [{ fonte: o.fonte as FonteSensor, t, pm }]
              : [];
          })
        : [],
      monitor: x.monitor === true,
      licenca: texto(x.lic),
      tolMs: tol !== null && tol > 0 ? Math.min(tol, 6 * 60) * 60_000 : TOLERANCIA_SENSOR_MS,
      flagsNaJanela: Array.isArray(x.fl) ? (x.fl as unknown[]).map(num).filter((v): v is number => v !== null) : [],
      serie: serieNum(x.serie, slots),
    });
  }

  const municipiosH0 = Number(m?.h0) || 0;
  const horasMun = Number(m?.horas) || 48;
  const municipios = new Map<string, (number | null)[]>();
  for (const [nome, serie] of Object.entries((m?.serie ?? {}) as Record<string, unknown>)) {
    municipios.set(nome, serieNum(serie, horasMun));
  }

  const horas = Number(e.horas) || 48;
  const estacoes: Estacao[] = [];
  for (const x of e.lista as Cru[]) {
    const lat = num(x.lat);
    const lon = num(x.lon);
    const id = num(x.id);
    if (lat === null || lon === null || id === null) continue;
    const p = projetar(lon, lat);
    if (!p) continue;
    const poluentes: Poluente[] = [];
    for (const q of Array.isArray(x.polu) ? (x.polu as Cru[]) : []) {
      const nome = texto(q.nome);
      if (!nome) continue;
      poluentes.push({
        nome,
        descricao: texto(q.desc),
        iqar: serieNum(q.iqar, horas),
        classe: serieNum(q.cl, horas),
        validado: serieNum(q.val, horas),
      });
    }
    const a = x.atual as Cru | null | undefined;
    const aIqar = a ? num(a.iqar) : null;
    const aT = a ? num(a.t) : null;
    estacoes.push({
      tipo: 'estacao',
      id,
      nome: texto(x.nome) ?? `Estação ${id}`,
      municipio: texto(x.mun),
      uf: texto(x.uf),
      lat,
      lon,
      mx: p[0],
      my: p[1],
      orgao: texto(x.orgao),
      ultima: num(x.ult),
      atual:
        a && aIqar !== null && aT !== null
          ? {
              t: aT,
              iqar: aIqar,
              faixa: faixaDoMonitorAr(num(a.cl)) ?? faixaIqar(aIqar),
              nomeClasse: texto(a.nomeCl),
              poluente: texto(a.pol),
            }
          : null,
      poluentes,
      horas: iqarPorHora(poluentes, horas),
    });
  }

  const fontes: Record<string, Fonte> = {};
  for (const [nome, f] of Object.entries((r.fontes ?? {}) as Record<string, Cru>)) {
    fontes[nome] = { em: num(f?.em), erro: texto(f?.erro), nota: texto(f?.nota), saldo: num(f?.saldo), gastoDia: num(f?.gastoDia) };
  }

  const gerado = typeof r.gerado === 'string' ? Date.parse(r.gerado) : NaN;
  return {
    gerado: Number.isFinite(gerado) ? gerado : null,
    sensores,
    t0: Number(s.t0) || 0,
    passoMin: Number(s.passoMin) || 5,
    slots,
    municipios,
    municipiosH0,
    estacoes,
    h0: Number(e.h0) || 0,
    horas,
    fontes,
  };
}

export function lerCams(cru: unknown): Cams {
  const r = cru as Cru;
  if (!r || !Array.isArray(r.celulas) || !Array.isArray(r.pm2_5)) throw new Error('cams.json sem `celulas` ou `pm2_5`');
  const horas = Number(r.horas) || 48;
  const celulas: { lon: number; lat: number }[] = [];
  const pm25: (number | null)[][] = [];
  (r.celulas as unknown[]).forEach((c, i) => {
    if (!Array.isArray(c) || num(c[0]) === null || num(c[1]) === null) return;
    celulas.push({ lon: c[0] as number, lat: c[1] as number });
    pm25.push(serieNum((r.pm2_5 as unknown[])[i], horas));
  });
  const gerado = typeof r.gerado === 'string' ? Date.parse(r.gerado) : NaN;
  return {
    gerado: Number.isFinite(gerado) ? gerado : null,
    run: num(r.run),
    passo: num(r.passo) ?? 0.8,
    h0: Number(r.h0) || 0,
    horas,
    celulas,
    pm25,
  };
}

const VARS: readonly VariavelCams[] = ['pm2_5', 'pm10', 'carbon_monoxide', 'ozone', 'nitrogen_dioxide', 'aerosol_optical_depth'];

export function lerCamsSeries(cru: unknown): CamsSeries {
  const r = cru as Cru;
  if (!r || typeof r.series !== 'object' || r.series === null) throw new Error('cams-series.json sem `series`');
  const horas = Number(r.horas) || 48;
  const series: Record<string, (number | null)[][]> = {};
  for (const v of VARS) {
    const lista = (r.series as Record<string, unknown>)[v];
    if (Array.isArray(lista)) series[v] = lista.map((s) => serieNum(s, horas));
  }
  return {
    h0: Number(r.h0) || 0,
    horas,
    vars: VARS.filter((v) => series[v]),
    unidades: (r.unidades ?? {}) as Record<string, string>,
    series,
  };
}

/** Os arquivos mudam a cada rodada; `no-cache` revalida no servidor (304 quando nada mudou). */
async function json(nome: string, signal?: AbortSignal): Promise<unknown> {
  return fetchWithRetry<unknown>(`${BASE_AR}/v1/${nome}`, { cache: 'no-cache', mode: 'cors', credentials: 'omit' }, {
    signal,
    read: (r) => r.json() as Promise<unknown>,
  });
}

export async function carregarPontos(signal?: AbortSignal): Promise<Pontos> {
  return lerPontos(await json('pontos.json', signal));
}

export async function carregarCams(signal?: AbortSignal): Promise<Cams> {
  return lerCams(await json('cams.json', signal));
}

export async function carregarCamsSeries(signal?: AbortSignal): Promise<CamsSeries> {
  return lerCamsSeries(await json('cams-series.json', signal));
}

/** Índice da célula que contém (lon, lat), ou -1. As células são centradas em múltiplos do passo. */
export function celulaEm(cams: Pick<Cams, 'celulas' | 'passo'>, lon: number, lat: number, indice?: Map<string, number>): number {
  const kx = Math.round(lon / cams.passo);
  const ky = Math.round(lat / cams.passo);
  const chave = `${kx}|${ky}`;
  if (indice) return indice.get(chave) ?? -1;
  return cams.celulas.findIndex((c) => Math.round(c.lon / cams.passo) === kx && Math.round(c.lat / cams.passo) === ky);
}

/** Índice (kx|ky → posição) para achar a célula de um clique sem varrer a grade. */
export function indiceDeCelulas(cams: Pick<Cams, 'celulas' | 'passo'>): Map<string, number> {
  const m = new Map<string, number>();
  cams.celulas.forEach((c, i) => m.set(`${Math.round(c.lon / cams.passo)}|${Math.round(c.lat / cams.passo)}`, i));
  return m;
}

/** Instantes de uma grade horária (para o eixo dos gráficos). */
export function instantesHorarios(h0: number, n: number): number[] {
  return Array.from({ length: n }, (_, i) => h0 + i * HOUR_MS);
}
